import { l as DocContext, k as jsxRuntimeExports } from "./doenetml-a_KitGQm.js";
import React__default, { useContext, useRef } from "react";
import { j as jxg, s as syncLayer, e as syncLineStrokeStyle, a as syncWithLabelToggle, f as syncLabelStrokeColor, b as syncLabelMaskCssStyle, c as computeLabelMaskCssStyle, d as attachLabelHoverHighlight, r as removeJXGEventHandlers } from "./JSXGraphRenderer-BMkZb_8Q.js";
import { u as useDoenetRenderer } from "./useDoenetRenderer-Ca2HvWV4.js";
import { BoardContext, POINT_LAYER_OFFSET, LINE_LAYER_OFFSET } from "./graph-gLVvVe_r.js";
import { c as characterizeOffGraphPoint, a as characterizeOffGraphCircleArc } from "./offGraphIndicators-CYoqQHuD.js";
import { n as normalizePointStyle, a as normalizePointSize, b as adjustPointLabelPosition, c as getEffectiveBoundingBox, d as getGraphCornerWithBuffer, e as calculatePointLabelAnchor } from "./graph-W6vrFcBu.js";
import { u as usePointerDragState, a as useBoardPointerTracking } from "./useBoardPointerTracking-DV6a8Wvg.js";
import { p as pointerEventToUserCoords } from "./dragThreshold-DuSSFrIU.js";
import { a as attachLineFamilyDragHandlers } from "./lineFamilyDragHandlers-tcr76K44.js";
import { a as resolveLineColor, r as resolveFillColor, c as resolveMarkerColor, b as resolveCanvasColor } from "./styleColors-Cknqcr0q.js";
import { s as styleToDash } from "./styleToDash-DWb71gbd.js";
import { b as buildFilledShapeAttributes } from "./buildGraphicalAttributes-C7PtyKL7.js";
import { g as getPatternFillAttributes } from "./fillPatterns-BQpfOUtY.js";
import { u as useDraggableRefs, a as useJSXGraphCleanup } from "./useJSXGraphCleanup-CX91oVW8.js";
const circle = React__default.memo(function Circle(props) {
  let { componentIdx, id, SVs, actions, callAction } = useDoenetRenderer(props);
  Circle.ignoreActionsWithoutCore = () => true;
  const board = useContext(BoardContext);
  let circleJXG = useRef(null);
  let indicatorJXG = useRef(null);
  const dragState = usePointerDragState();
  let radiusAtDown = useRef(null);
  let throughAnglesAtDown = useRef(null);
  let previousWithLabel = useRef(null);
  let previousPointLabelPosition = useRef(null);
  let centerCoords = useRef(null);
  const dragCoordination = {
    draggedTag: useRef(null),
    downOnTag: useRef(null)
  };
  const {
    lastPositionFromCore: lastCenterFromCore,
    fixed,
    fixLocation
  } = useDraggableRefs(SVs, SVs.numericalCenter);
  let throughAnglesFromCore = useRef(null);
  throughAnglesFromCore.current = SVs.throughAngles;
  let displayOffGraphIndicator = useRef(false);
  let offGraphIndicatorOrientation = useRef([0, 0]);
  let offGraphIndicatorCoords = useRef([0, 0]);
  let offGraphIndicatorOffsetAtDown = useRef([0, 0]);
  const { darkMode } = useContext(DocContext) || {};
  useBoardPointerTracking(board, dragState);
  useJSXGraphCleanup({
    objectRef: circleJXG,
    destroy: () => deleteCircleJXG()
  });
  function createCircleJXG() {
    if (board === null) {
      return null;
    }
    if (!(Number.isFinite(SVs.numericalCenter[0]) && Number.isFinite(SVs.numericalCenter[1]) && SVs.numericalRadius > 0)) {
      return null;
    }
    const lineColor = resolveLineColor(SVs.selectedStyle, darkMode);
    const markerColor = resolveMarkerColor(SVs.selectedStyle, darkMode);
    let withlabel = SVs.labelForGraph !== "";
    var jsxCircleAttributes = buildFilledShapeAttributes({
      SVs,
      layerOffset: LINE_LAYER_OFFSET,
      fixed: fixed.current,
      fixLocation: fixLocation.current,
      darkMode
    });
    if (!SVs.filled) {
      jsxCircleAttributes.fillColor = "none";
      jsxCircleAttributes.highlightFillColor = "none";
    } else if (board) {
      Object.assign(
        jsxCircleAttributes,
        getPatternFillAttributes({
          defsEl: board.renderer.defs,
          boardId: board.container.id,
          fillStyle: SVs.selectedStyle.fillStyle ?? "solid",
          fillColor: jsxCircleAttributes.fillColor,
          fillOpacity: SVs.selectedStyle.fillOpacity,
          canvasColor: resolveCanvasColor(darkMode),
          fillPatternOpacity: SVs.selectedStyle.fillPatternOpacity,
          highlightFillOpacity: SVs.selectedStyle.fillOpacity * 0.5
        })
      );
    }
    if (SVs.filled) {
      jsxCircleAttributes.hasInnerPoints = true;
    }
    jsxCircleAttributes.label = {
      highlight: false,
      ...computeLabelMaskCssStyle({
        layer: SVs.layer,
        masked: SVs.maskLabel
      }),
      highlightStrokeOpacity: 1
    };
    if (SVs.labelHasLatex) {
      jsxCircleAttributes.label.useMathJax = true;
    }
    if (SVs.labelForGraph !== "") {
      if (SVs.applyStyleToLabel) {
        jsxCircleAttributes.label.strokeColor = lineColor;
      } else {
        jsxCircleAttributes.label.strokeColor = "var(--canvasText)";
      }
    }
    circleJXG.current = board.create(
      "circle",
      [[...SVs.numericalCenter], SVs.numericalRadius],
      jsxCircleAttributes
    );
    circleJXG.current.isDraggable = !fixLocation.current;
    attachLabelHoverHighlight({
      hoverTargetJXG: circleJXG.current,
      getLabelJXG: () => circleJXG.current?.label,
      ...computeLabelMaskCssStyle({
        layer: SVs.layer,
        masked: SVs.maskLabel
      }),
      board
    });
    let jsxPointAttributes = {
      name: SVs.labelForGraph,
      visible: !SVs.hidden && displayOffGraphIndicator.current,
      withlabel,
      fixed: fixed.current,
      layer: 10 * SVs.layer + POINT_LAYER_OFFSET,
      fillColor: markerColor,
      strokeColor: "none",
      strokeOpacity: SVs.selectedStyle.markerOpacity,
      fillOpacity: SVs.selectedStyle.markerOpacity,
      highlightFillColor: "var(--mainGray)",
      highlightStrokeColor: "var(--lightBlue)",
      size: normalizePointSize(
        SVs.selectedStyle.markerSize,
        SVs.selectedStyle.markerStyle
      ),
      face: normalizePointStyle(
        SVs.selectedStyle.markerStyle,
        offGraphIndicatorOrientation.current
      ),
      highlight: !fixLocation.current,
      showinfobox: false
    };
    if (withlabel) {
      let labelPosition = adjustPointLabelPosition(
        "upperright",
        offGraphIndicatorOrientation.current
      );
      previousPointLabelPosition.current = labelPosition;
      let { offset, anchorx, anchory } = calculatePointLabelAnchor(labelPosition);
      jsxPointAttributes.label = {
        offset,
        anchorx,
        anchory,
        highlight: false,
        ...computeLabelMaskCssStyle({
          layer: SVs.layer,
          masked: SVs.maskLabel
        }),
        highlightStrokeOpacity: 1
      };
      if (SVs.labelHasLatex) {
        jsxPointAttributes.label.useMathJax = true;
      }
      if (SVs.applyStyleToLabel) {
        jsxPointAttributes.label.strokeColor = markerColor;
      } else {
        jsxPointAttributes.label.strokeColor = "var(--canvasText)";
      }
    } else {
      jsxPointAttributes.label = {
        highlight: false,
        ...computeLabelMaskCssStyle({
          layer: SVs.layer,
          masked: SVs.maskLabel
        }),
        highlightStrokeOpacity: 1
      };
      if (SVs.labelHasLatex) {
        jsxPointAttributes.label.useMathJax = true;
      }
    }
    indicatorJXG.current = board.create(
      "point",
      [...offGraphIndicatorCoords.current],
      jsxPointAttributes
    );
    if (!indicatorJXG.current || !circleJXG.current) {
      return;
    }
    indicatorJXG.current.isDraggable = !fixLocation.current;
    attachLabelHoverHighlight({
      hoverTargetJXG: indicatorJXG.current,
      getLabelJXG: () => indicatorJXG.current?.label,
      ...computeLabelMaskCssStyle({
        layer: SVs.layer,
        masked: SVs.maskLabel
      }),
      board
    });
    function buildCircleCommitArgs() {
      if (!centerCoords.current || !Number.isFinite(centerCoords.current[0]) || !Number.isFinite(centerCoords.current[1])) {
        return null;
      }
      return {
        center: centerCoords.current,
        radius: radiusAtDown.current,
        throughAngles: throughAnglesAtDown.current
      };
    }
    function captureCircleSnapshot() {
      if (!circleJXG.current) {
        return null;
      }
      radiusAtDown.current = circleJXG.current.radius;
      throughAnglesAtDown.current = [...throughAnglesFromCore.current];
      return [...circleJXG.current.center.coords.scrCoords];
    }
    attachLineFamilyDragHandlers({
      jxg: circleJXG.current,
      tag: 0,
      dragState,
      coordination: dragCoordination,
      componentIdx,
      callAction,
      fixedRef: fixed,
      actions: {
        move: actions.moveCircle,
        focus: actions.circleFocused,
        click: actions.circleClicked
      },
      snapshot: captureCircleSnapshot,
      dispatchTransientBelowThreshold: true,
      buildTransientMoveArgs: (e, snap) => {
        let viaPointer = e.type === "pointermove";
        if (viaPointer && dragState.pointerAtDown.current && snap) {
          centerCoords.current = pointerEventToUserCoords(
            e,
            dragState.pointerAtDown.current,
            snap,
            board
          );
        } else {
          centerCoords.current = [
            circleJXG.current.center.X(),
            circleJXG.current.center.Y()
          ];
        }
        const base = buildCircleCommitArgs();
        if (!base) {
          return null;
        }
        return { ...base, transient: true, skippable: true };
      },
      buildCommitMoveArgs: () => buildCircleCommitArgs(),
      onDragApplied: () => {
        circleJXG.current.center.coords.setCoordinates(
          jxg.COORDS_BY_USER,
          [...lastCenterFromCore.current]
        );
      }
    });
    attachLineFamilyDragHandlers({
      jxg: indicatorJXG.current,
      tag: 1,
      dragState,
      coordination: dragCoordination,
      componentIdx,
      callAction,
      fixedRef: fixed,
      actions: {
        move: actions.moveCircle,
        focus: actions.circleFocused,
        click: actions.circleClicked
      },
      snapshot: captureCircleSnapshot,
      dispatchTransientBelowThreshold: true,
      buildTransientMoveArgs: () => {
        centerCoords.current = [
          indicatorJXG.current.X() + offGraphIndicatorOffsetAtDown.current[0],
          indicatorJXG.current.Y() + offGraphIndicatorOffsetAtDown.current[1]
        ];
        const base = buildCircleCommitArgs();
        if (!base) {
          return null;
        }
        return { ...base, transient: true, skippable: true };
      },
      buildCommitMoveArgs: () => buildCircleCommitArgs(),
      onDownExtra: () => {
        if (!offGraphIndicatorOrientation.current || !radiusAtDown.current) {
          return;
        }
        let { flippedX, flippedY } = getEffectiveBoundingBox(board);
        let xSign = flippedX ? -1 : 1;
        let ySign = flippedY ? -1 : 1;
        if (offGraphIndicatorOrientation.current[0] === 0 || offGraphIndicatorOrientation.current[1] === 0) {
          offGraphIndicatorOffsetAtDown.current = [
            xSign * offGraphIndicatorOrientation.current[0] * radiusAtDown.current,
            ySign * offGraphIndicatorOrientation.current[1] * radiusAtDown.current
          ];
        } else {
          let sqrt2 = Math.sqrt(2);
          offGraphIndicatorOffsetAtDown.current = [
            xSign / sqrt2 * offGraphIndicatorOrientation.current[0] * radiusAtDown.current,
            ySign / sqrt2 * offGraphIndicatorOrientation.current[1] * radiusAtDown.current
          ];
        }
      }
    });
    previousWithLabel.current = SVs.labelForGraph !== "";
    return circleJXG.current;
  }
  function deleteCircleJXG() {
    if (!circleJXG.current || !indicatorJXG.current) {
      return;
    }
    removeJXGEventHandlers(indicatorJXG.current);
    board?.removeObject(indicatorJXG.current);
    indicatorJXG.current = null;
    removeJXGEventHandlers(circleJXG.current);
    board?.removeObject(circleJXG.current);
    circleJXG.current = null;
  }
  if (board) {
    lastCenterFromCore.current = [...SVs.numericalCenter];
    displayOffGraphIndicator.current = false;
    offGraphIndicatorOrientation.current = [0, 0];
    offGraphIndicatorCoords.current = [0, 0];
    if (!SVs.hideOffGraphIndicator) {
      let centerOffResults = characterizeOffGraphPoint(
        lastCenterFromCore.current,
        board
      );
      if (centerOffResults.needIndicator) {
        if (!offGraphIndicatorCoords.current) {
          return;
        }
        let centerSides = centerOffResults.indicatorSides;
        let { flippedX, flippedY } = getEffectiveBoundingBox(board);
        let xSign = flippedX ? -1 : 1;
        let ySign = flippedY ? -1 : 1;
        if (centerSides[0] === 1) {
          if (centerSides[1] === 1) {
            let lowerLeftPoint = [...lastCenterFromCore.current];
            lowerLeftPoint[0] -= SVs.numericalRadius * xSign;
            lowerLeftPoint[1] -= SVs.numericalRadius * ySign;
            let lowerLeftOffResults = characterizeOffGraphPoint(
              lowerLeftPoint,
              board
            );
            if (lowerLeftOffResults.needIndicator) {
              displayOffGraphIndicator.current = true;
              offGraphIndicatorOrientation.current = [1, 1];
              offGraphIndicatorCoords.current = getGraphCornerWithBuffer(board, [1, 1]);
            } else {
              let arcResults = characterizeOffGraphCircleArc({
                center: SVs.numericalCenter,
                radius: SVs.numericalRadius,
                directionToCheck: [1, 1],
                board
              });
              if (arcResults.needIndicator) {
                displayOffGraphIndicator.current = true;
                offGraphIndicatorOrientation.current = arcResults.indicatorSides;
                offGraphIndicatorCoords.current = arcResults.indicatorCoords;
              }
            }
          } else if (centerSides[1] === -1) {
            let upperLeftPoint = [...lastCenterFromCore.current];
            upperLeftPoint[0] -= SVs.numericalRadius * xSign;
            upperLeftPoint[1] += SVs.numericalRadius * ySign;
            let upperLeftOffResults = characterizeOffGraphPoint(
              upperLeftPoint,
              board
            );
            if (upperLeftOffResults.needIndicator) {
              displayOffGraphIndicator.current = true;
              offGraphIndicatorOrientation.current = [1, -1];
              offGraphIndicatorCoords.current = getGraphCornerWithBuffer(board, [1, -1]);
            } else {
              let arcResults = characterizeOffGraphCircleArc({
                center: SVs.numericalCenter,
                radius: SVs.numericalRadius,
                directionToCheck: [1, -1],
                board
              });
              if (arcResults.needIndicator) {
                displayOffGraphIndicator.current = true;
                offGraphIndicatorOrientation.current = arcResults.indicatorSides;
                offGraphIndicatorCoords.current = arcResults.indicatorCoords;
              }
            }
          } else {
            let leftPoint = [...lastCenterFromCore.current];
            leftPoint[0] -= SVs.numericalRadius * xSign;
            let leftOffResults = characterizeOffGraphPoint(
              leftPoint,
              board
            );
            if (leftOffResults.needIndicator) {
              displayOffGraphIndicator.current = true;
              offGraphIndicatorOrientation.current = leftOffResults.indicatorSides;
              offGraphIndicatorCoords.current = leftOffResults.indicatorCoords;
            }
          }
        } else if (centerSides[0] === -1) {
          if (centerSides[1] === 1) {
            let lowerRightPoint = [...lastCenterFromCore.current];
            lowerRightPoint[0] += SVs.numericalRadius * xSign;
            lowerRightPoint[1] -= SVs.numericalRadius * ySign;
            let lowerRightOffResults = characterizeOffGraphPoint(
              lowerRightPoint,
              board
            );
            if (lowerRightOffResults.needIndicator) {
              displayOffGraphIndicator.current = true;
              offGraphIndicatorOrientation.current = [-1, 1];
              offGraphIndicatorCoords.current = getGraphCornerWithBuffer(board, [-1, 1]);
            } else {
              let arcResults = characterizeOffGraphCircleArc({
                center: SVs.numericalCenter,
                radius: SVs.numericalRadius,
                directionToCheck: [-1, 1],
                board
              });
              if (arcResults.needIndicator) {
                displayOffGraphIndicator.current = true;
                offGraphIndicatorOrientation.current = arcResults.indicatorSides;
                offGraphIndicatorCoords.current = arcResults.indicatorCoords;
              }
            }
          } else if (centerSides[1] === -1) {
            let upperRightPoint = [...lastCenterFromCore.current];
            upperRightPoint[0] += SVs.numericalRadius * xSign;
            upperRightPoint[1] += SVs.numericalRadius * ySign;
            let upperRightOffResults = characterizeOffGraphPoint(
              upperRightPoint,
              board
            );
            if (upperRightOffResults.needIndicator) {
              displayOffGraphIndicator.current = true;
              offGraphIndicatorOrientation.current = [-1, -1];
              offGraphIndicatorCoords.current = getGraphCornerWithBuffer(board, [-1, -1]);
            } else {
              let arcResults = characterizeOffGraphCircleArc({
                center: SVs.numericalCenter,
                radius: SVs.numericalRadius,
                directionToCheck: [-1, -1],
                board
              });
              if (arcResults.needIndicator) {
                displayOffGraphIndicator.current = true;
                offGraphIndicatorOrientation.current = arcResults.indicatorSides;
                offGraphIndicatorCoords.current = arcResults.indicatorCoords;
              }
            }
          } else {
            let rightPoint = [...lastCenterFromCore.current];
            rightPoint[0] += SVs.numericalRadius * xSign;
            let rightOffResults = characterizeOffGraphPoint(
              rightPoint,
              board
            );
            if (rightOffResults.needIndicator) {
              displayOffGraphIndicator.current = true;
              offGraphIndicatorOrientation.current = rightOffResults.indicatorSides;
              offGraphIndicatorCoords.current = rightOffResults.indicatorCoords;
            }
          }
        } else if (centerSides[1] === 1) {
          let bottomPoint = [...lastCenterFromCore.current];
          bottomPoint[1] -= SVs.numericalRadius * xSign;
          let bottomOffResults = characterizeOffGraphPoint(
            bottomPoint,
            board
          );
          if (bottomOffResults.needIndicator) {
            displayOffGraphIndicator.current = true;
            offGraphIndicatorOrientation.current = bottomOffResults.indicatorSides;
            offGraphIndicatorCoords.current = bottomOffResults.indicatorCoords;
          }
        } else {
          let topPoint = [...lastCenterFromCore.current];
          topPoint[1] += SVs.numericalRadius * xSign;
          let topOffResults = characterizeOffGraphPoint(
            topPoint,
            board
          );
          if (topOffResults.needIndicator) {
            displayOffGraphIndicator.current = true;
            offGraphIndicatorOrientation.current = topOffResults.indicatorSides;
            offGraphIndicatorCoords.current = topOffResults.indicatorCoords;
          }
        }
      }
    }
    if (!circleJXG.current) {
      createCircleJXG();
    } else if (!(Number.isFinite(SVs.numericalCenter[0]) && Number.isFinite(SVs.numericalCenter[1]) && SVs.numericalRadius > 0)) {
      deleteCircleJXG();
    } else if (indicatorJXG.current) {
      if (board.updateQuality === board.BOARD_QUALITY_LOW) {
        board.itemsRenderedLowQuality[id] = circleJXG.current;
      }
      let validCoords = SVs.numericalCenter.every(
        (x) => Number.isFinite(x)
      );
      circleJXG.current.center.coords.setCoordinates(jxg.COORDS_BY_USER, [
        ...SVs.numericalCenter
      ]);
      circleJXG.current.setRadius(SVs.numericalRadius);
      let visible = !SVs.hidden;
      if (validCoords) {
        circleJXG.current.visProp["visible"] = visible;
        circleJXG.current.visPropCalc["visible"] = visible;
      } else {
        circleJXG.current.visProp["visible"] = false;
        circleJXG.current.visPropCalc["visible"] = false;
      }
      circleJXG.current.visProp.fixed = fixed.current;
      circleJXG.current.visProp.highlight = !fixLocation.current;
      circleJXG.current.isDraggable = !fixLocation.current;
      syncLayer(circleJXG.current, SVs.layer, LINE_LAYER_OFFSET);
      const lineColor = resolveLineColor(SVs.selectedStyle, darkMode);
      const resolvedFillColor = SVs.filled ? resolveFillColor(SVs.selectedStyle, darkMode) : "none";
      const fillAttributes = SVs.filled && board ? getPatternFillAttributes({
        defsEl: board.renderer.defs,
        boardId: board.container.id,
        fillStyle: SVs.selectedStyle.fillStyle ?? "solid",
        fillColor: resolvedFillColor,
        fillOpacity: SVs.selectedStyle.fillOpacity,
        canvasColor: resolveCanvasColor(darkMode),
        fillPatternOpacity: SVs.selectedStyle.fillPatternOpacity,
        highlightFillOpacity: SVs.selectedStyle.fillOpacity * 0.5
      }) : {
        fillColor: resolvedFillColor,
        fillOpacity: SVs.selectedStyle.fillOpacity,
        highlightFillColor: resolvedFillColor,
        highlightFillOpacity: SVs.selectedStyle.fillOpacity * 0.5
      };
      syncLineStrokeStyle(circleJXG.current, {
        lineColor,
        lineWidth: SVs.selectedStyle.lineWidth,
        lineOpacity: SVs.selectedStyle.lineOpacity,
        dash: styleToDash(SVs.selectedStyle.lineStyle)
      });
      if (circleJXG.current.visProp.fillcolor !== fillAttributes.fillColor) {
        circleJXG.current.visProp.fillcolor = fillAttributes.fillColor;
        circleJXG.current.visProp.highlightfillcolor = fillAttributes.highlightFillColor;
        circleJXG.current.visProp.hasinnerpoints = SVs.filled;
      }
      if (circleJXG.current.visProp.fillopacity !== fillAttributes.fillOpacity) {
        circleJXG.current.visProp.fillopacity = fillAttributes.fillOpacity;
        circleJXG.current.visProp.highlightfillopacity = fillAttributes.highlightFillOpacity;
      }
      circleJXG.current.name = SVs.labelForGraph;
      const withlabel = syncWithLabelToggle(
        circleJXG.current,
        SVs.labelForGraph,
        previousWithLabel
      );
      circleJXG.current.needsUpdate = true;
      circleJXG.current.update();
      if (circleJXG.current.hasLabel && circleJXG.current.label) {
        const label = circleJXG.current.label;
        syncLabelStrokeColor(label, SVs.applyStyleToLabel, lineColor);
        syncLabelMaskCssStyle(label, SVs.layer, {
          maskLabel: SVs.maskLabel
        });
        label.needsUpdate = true;
        label.update();
      }
      let showIndicator = displayOffGraphIndicator.current && !SVs.hidden;
      let actuallyChangedVisibility = indicatorJXG.current.visProp["visible"] !== showIndicator;
      indicatorJXG.current.visProp["visible"] = showIndicator;
      indicatorJXG.current.visPropCalc["visible"] = showIndicator;
      if (showIndicator && indicatorJXG.current) {
        indicatorJXG.current.coords.setCoordinates(
          jxg.COORDS_BY_USER,
          offGraphIndicatorCoords.current
        );
        syncLayer(indicatorJXG.current, SVs.layer, POINT_LAYER_OFFSET);
        indicatorJXG.current.visProp.highlight = !fixLocation.current;
        indicatorJXG.current.visProp.fixed = fixed.current;
        indicatorJXG.current.isDraggable = !fixLocation.current;
        const markerColor = resolveMarkerColor(
          SVs.selectedStyle,
          darkMode
        );
        if (indicatorJXG.current.visProp.fillcolor !== markerColor) {
          indicatorJXG.current.visProp.fillcolor = markerColor;
        }
        if (indicatorJXG.current.visProp.strokeopacity !== SVs.selectedStyle.markerOpacity) {
          indicatorJXG.current.visProp.strokeopacity = SVs.selectedStyle.markerOpacity;
          indicatorJXG.current.visProp.fillopacity = SVs.selectedStyle.markerOpacity;
        }
        let newFace = normalizePointStyle(
          SVs.selectedStyle.markerStyle,
          offGraphIndicatorOrientation.current
        );
        if (indicatorJXG.current.visProp.face !== newFace) {
          indicatorJXG.current.setAttribute({ face: newFace });
        }
        let newSize = normalizePointSize(
          SVs.selectedStyle.markerSize,
          SVs.selectedStyle.markerStyle
        );
        if (indicatorJXG.current.visProp.size !== newSize) {
          indicatorJXG.current.setAttribute({ size: newSize });
        }
        indicatorJXG.current.name = SVs.labelForGraph;
        if (withlabel != previousWithLabel.current) {
          indicatorJXG.current.setAttribute({ withlabel });
        }
        if (indicatorJXG.current.hasLabel && indicatorJXG.current.label) {
          const label = indicatorJXG.current.label;
          label.needsUpdate = true;
          syncLabelStrokeColor(
            label,
            SVs.applyStyleToLabel,
            markerColor
          );
          syncLabelMaskCssStyle(label, SVs.layer, {
            maskLabel: SVs.maskLabel
          });
          let labelPosition = adjustPointLabelPosition(
            "upperright",
            offGraphIndicatorOrientation.current
          );
          if (labelPosition !== previousPointLabelPosition.current) {
            let { offset, anchorx, anchory } = calculatePointLabelAnchor(labelPosition);
            label.visProp.anchorx = anchorx;
            label.visProp.anchory = anchory;
            label.visProp.offset = offset;
            previousPointLabelPosition.current = labelPosition;
            label.fullUpdate();
          } else {
            label.update();
          }
        }
      }
      if (showIndicator || actuallyChangedVisibility) {
        indicatorJXG.current.fullUpdate();
      } else {
        indicatorJXG.current.update();
      }
      board.updateRenderer();
    }
  }
  if (SVs.hidden) {
    return null;
  }
  return /* @__PURE__ */ jsxRuntimeExports.jsx("span", { id });
});
export {
  circle as default
};
